import { useEffect, useMemo, useRef, useState } from "react";
import { uid } from "../../lib/uid";
import { http } from "../../lib/api";

export const emptyEntry = () => ({ key: uid(), id: null, subcontractor_id: "", buy_price: "", price_valid_from: "", valid_to: "" });
export const parseBuy = (v) => { const t = String(v ?? "").trim(); if (!t) return 0; return t.includes(",") ? (parseFloat(t.replace(/\./g, "").replace(",", ".")) || 0) : (parseFloat(t) || 0); };
const filled = (e) => e.id || e.subcontractor_id || e.buy_price;
const cardToEntry = (c) => ({ key: c.id, id: c.id, subcontractor_id: c.subcontractor_id || "", buy_price: String(c.buy_price ?? ""), price_valid_from: c.price_valid_from || "", valid_to: c.valid_to || "" });

// Şube hizmet satırlarının yerel düzenleme durumu: {serviceId: [entry…]}
export function useServiceRows({ services, asgs, branchSubIds, filterSub }) {
  const cardsOf = (sid) => asgs.filter((a) => a.service_id === sid);
  const init = () => Object.fromEntries(services.map((s) => {
    const cs = cardsOf(s.id);
    const single = branchSubIds.length === 1 ? branchSubIds[0] : "";
    return [s.id, cs.length ? cs.map(cardToEntry) : [{ ...emptyEntry(), subcontractor_id: single }]];
  }));
  const [rows, setRows] = useState(init);
  const subKey = branchSubIds.join(",");
  const initRef = useRef(init);
  initRef.current = init;
  useEffect(() => { setRows(initRef.current()); }, [asgs, services.length, subKey]);

  const setEntry = (sid, key, patch) => setRows((prev) => {
    const list = prev[sid] || [];
    if (!list.some((e) => e.key === key)) return { ...prev, [sid]: [...list.filter(filled), { ...emptyEntry(), key, subcontractor_id: filterSub, ...patch }] };
    return { ...prev, [sid]: list.map((e) => (e.key === key ? { ...e, ...patch } : e)) };
  });
  // taşeron seçildiğinde boş kalan diğer alt hizmetlere aynı taşeronu öner (manuel değiştirilebilir)
  const pickSub = (sid, key, v) => setRows((prev) => {
    const base = (prev[sid] || []).some((e) => e.key === key) ? prev[sid] : [...(prev[sid] || []).filter(filled), { ...emptyEntry(), key }];
    const next = { ...prev, [sid]: base.map((e) => (e.key === key ? { ...e, subcontractor_id: v } : e)) };
    if (v) for (const s of services) {
      if (s.id === sid) continue;
      const list = next[s.id] || [emptyEntry()];
      if (list.length === 1 && !list[0].id && !list[0].subcontractor_id) next[s.id] = [{ ...list[0], subcontractor_id: v }];
    }
    return next;
  });
  // bir satırdaki taşeron + fiyat geçerlilik tarihini TÜM alt hizmetlere uygula: aynı taşeronlu satır varsa tarihi güncelle, boş satır varsa doldur, yoksa yeni satır ekle
  const applyToAll = (sid, key) => {
    const src = (rows[sid] || []).find((e) => e.key === key);
    if (!src?.subcontractor_id) return 0;
    const patch = { subcontractor_id: src.subcontractor_id, price_valid_from: src.price_valid_from };
    const next = { ...rows };
    let n = 0;
    for (const s of services) {
      if (s.id === sid) continue;
      const list = next[s.id] || [emptyEntry()];
      const target = list.find((e) => e.subcontractor_id === src.subcontractor_id) || list.find((e) => !e.id && !e.subcontractor_id);
      next[s.id] = target ? list.map((e) => (e === target ? { ...e, ...patch } : e)) : [...list.filter(filled), { ...emptyEntry(), ...patch }];
      n++;
    }
    setRows(next);
    return n;
  };
  const addEntry = (sid) => setRows((prev) => ({ ...prev, [sid]: [...prev[sid], emptyEntry()] }));
  const removeLocal = (sid, key) => setRows((prev) => { const list = prev[sid].filter((x) => x.key !== key); return { ...prev, [sid]: list.length ? list : [emptyEntry()] }; });
  const reset = () => setRows(initRef.current());   // İptal: kaydedilmemiş yerel değişiklikleri at, kayıtlı kartlara dön
  const sorted = useMemo(() => [...services].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")), [services]);
  return { rows, cardsOf, sorted, setEntry, pickSub, applyToAll, addEntry, removeLocal, reset };
}

// Tek satırın kaydedilecek değişikliğini hesapla: null | {kind:"upsert"|"end", …}
export function rowChange(e, card) {
  const buy = parseBuy(e.buy_price);
  const changed = !card ? (e.subcontractor_id || buy) : (e.subcontractor_id !== (card.subcontractor_id || "") || buy !== (card.buy_price || 0) || (e.price_valid_from && e.price_valid_from !== card.price_valid_from));
  const endChanged = card && (e.valid_to || "") !== (card.valid_to || "");
  if (changed) return { kind: "upsert", buy, replaceCard: card && e.subcontractor_id !== (card.subcontractor_id || "") ? card : null };
  if (endChanged) return { kind: "end", card };
  return null;
}

// Değişen satırları sunucuya yazar; kaydedilen satır sayısını döner
export async function persistRows({ sorted, rows, cardsOf, locId }) {
  let n = 0;
  for (const s of sorted) {
    const cs = cardsOf(s.id);
    for (const e of rows[s.id] || []) {
      const ch = rowChange(e, cs.find((x) => x.id === e.id));
      if (!ch) continue;
      if (ch.kind === "upsert") {
        await http.post("/assignments", { service_id: s.id, location_ids: [locId], subcontractor_id: e.subcontractor_id || null, buy_price: ch.buy, sell_price: 0, price_valid_from: e.price_valid_from || "", valid_to: e.valid_to || "" });
        if (ch.replaceCard) await http.delete(`/assignments/${ch.replaceCard.id}`);
      } else {
        await http.post(`/assignments/${ch.card.id}/end`, { valid_to: e.valid_to || "" });
      }
      n++;
    }
  }
  return n;
}
